import React, { useCallback, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { labels, nav, projects } from '../data/content';
import Section from './Section';
import Rich from './Rich';
import Lightbox from './Lightbox';
import { FOCUS_RING, LABEL } from './styles';

function LockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function ProjectCard({ project, slideSrc, onOpenSlide }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const detailId = `${project.id}-detail`;

  return (
    <article className="rounded-sm border border-stone-200 bg-white">
      {/* 제목 버튼의 클릭 영역을 카드 윗부분 전체로 넓힌다 */}
      <div className="relative p-6 transition-colors hover:bg-stone-50/70 md:p-8">
        {/* 수치는 좁은 화면에서 제목 위, sm 이상에서 오른쪽 */}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div className="min-w-0">
            <h3 className="text-balance text-lg font-bold tracking-tight md:text-xl">
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-controls={open ? detailId : undefined}
                className="text-left after:absolute after:inset-0 after:rounded-sm after:content-[''] focus-visible:outline-none focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-stone-900"
              >
                {t(project.name)}
              </button>
            </h3>
            <p className="mt-1.5 text-xs tabular-nums text-stone-500">{`${t(project.org)} · ${t(project.period)}`}</p>
          </div>
          {project.metric && (
            <div className="flex max-w-full flex-row flex-wrap items-baseline gap-x-2 self-start rounded-sm border border-stone-200 bg-stone-100/80 px-3 py-1.5 text-stone-800 sm:shrink-0 sm:flex-col sm:flex-nowrap sm:items-end sm:gap-0 sm:px-4 sm:py-2">
              <span className="whitespace-nowrap text-sm font-semibold leading-none tracking-tight tabular-nums sm:text-base">
                {t(project.metric.value)}
              </span>
              <span className="text-xs leading-snug text-stone-500 sm:mt-1 sm:max-w-[10rem] sm:text-right">
                {t(project.metric.label)}
              </span>
            </div>
          )}
        </div>

        <p className="mt-4 max-w-3xl text-pretty leading-relaxed text-stone-600">{t(project.summary)}</p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {project.skills.map((s) => (
            <span key={s} className="whitespace-nowrap rounded-full border border-stone-200 px-2 py-0.5 text-xs text-stone-600">
              {s}
            </span>
          ))}
        </div>

        {project.confidential && (
          <p className="mt-4 flex items-center gap-1.5 text-xs text-stone-500">
            <LockIcon />
            {t(labels.confidential)}
          </p>
        )}

        <p className="mt-5 text-sm font-medium text-stone-900 underline underline-offset-4" aria-hidden="true">
          {open ? t(labels.closeDetail) : t(labels.viewDetail)}
        </p>
      </div>

      {open && (
        <div id={detailId} className="border-t border-stone-100 px-6 pb-8 pt-2 md:px-8">
          <div className="mt-6 grid gap-8 md:grid-cols-[12rem_1fr]">
            <section className="min-w-0">
              <h4 className={`${LABEL} mb-3`}>{t(labels.role)}</h4>
              <p className="text-[15px] leading-6 text-stone-700">{t(project.role)}</p>
            </section>
            <section className="min-w-0 max-w-[65ch]">
              <h4 className={`${LABEL} mb-3`}>{t(labels.results)}</h4>
              <ul className="space-y-2.5">
                {project.results.map((r, i) => (
                  <li key={i} className="flex gap-2.5 text-[15px] leading-6 text-stone-600">
                    <span className="text-stone-400" aria-hidden="true">
                      —
                    </span>
                    <span className="text-pretty">
                      <Rich text={t(r)} />
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          {project.slides && (
            <div className="mt-10">
              <h4 className={`${LABEL} mb-4`}>{t(labels.slides)}</h4>
              <div className="grid gap-4 sm:grid-cols-2">
                {project.slides.map((file, i) => (
                  <button
                    key={file}
                    type="button"
                    onClick={() => onOpenSlide(project, i)}
                    aria-label={`${t(project.name)} ${i + 1} — ${t(labels.enlarge)}`}
                    className={`group block cursor-zoom-in overflow-hidden rounded-sm border border-stone-200 bg-stone-50 ${FOCUS_RING}`}
                  >
                    <img
                      src={slideSrc(file)}
                      alt=""
                      loading="lazy"
                      width={1440}
                      height={810}
                      className="aspect-video h-full w-full object-contain transition-opacity duration-200 group-hover:opacity-85"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function Projects() {
  const { lang, t } = useLanguage();
  const [viewer, setViewer] = useState(null);

  const slideSrc = (file) => `/slides/${lang === 'en' ? 'en/' : ''}${file}`;

  const openSlide = (project, index) =>
    setViewer({
      index,
      title: t(project.name),
      images: project.slides.map((file, i) => ({ src: slideSrc(file), alt: `${t(project.name)} ${i + 1}` })),
    });

  const closeViewer = useCallback(() => setViewer(null), []);
  const changeIndex = useCallback((index) => setViewer((v) => (v ? { ...v, index } : v)), []);

  return (
    <Section id="projects" title={t(nav.projects)}>
      <div className="space-y-6">
        {projects.map((p) => (
          <ProjectCard key={p.id} project={p} slideSrc={slideSrc} onOpenSlide={openSlide} />
        ))}
      </div>

      {viewer && (
        <Lightbox
          title={viewer.title}
          images={viewer.images}
          index={viewer.index}
          onIndexChange={changeIndex}
          onClose={closeViewer}
        />
      )}
    </Section>
  );
}
